import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy,
  SimpleChanges,
  input,
} from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { Subscription, from, map, switchMap } from 'rxjs';
import {
  IBooking,
  IStopTimeDisplay,
  ITrip,
  ITripDetail,
  ITripStop,
  ITripsRefreshEvent,
  IUserDriver,
  IVehicle,
  IBase,
  FilterOptionByIdLoader,
  FilterOptionLoader,
  ITown,
} from 'src/app/models';
import { Colors, ExpeditionStatus, ExpeditionType, ServiceType, TripsViewMode } from 'src/app/models/enums';
import { APP } from 'src/app/config/app';
import {
  RouteService,
  StorageService,
  TripsService,
  UsersService,
  UtilsService,
  VehiclesService,
} from 'src/app/services';
import { ServiceInfoService } from 'src/app/services/service-info/service-info.service';
import {
  driverByIdLoader,
  driverLoader,
  driverOption,
  routeByIdLoader,
  routeLoader,
  routeOption,
  serviceByIdLoader,
  serviceLoader,
  serviceOption,
  vehicleByIdLoader,
  vehicleLoader,
  vehicleOption,
} from 'src/app/shared/filter-panel/filterOptionSources';
import { LanguageService } from 'src/app/services/language/language.service';
import { LiteralService } from 'src/app/services/literal/literal.service';
import { TooltipPosition } from 'src/app/shared/tooltip/tooltip-position';
import { initialTripsState } from 'src/app/store/initialisation/trips';
import { images } from 'src/images';
import {
  ITripsFilters,
  TRIP_FILTER_KEYS,
  deserializeTripsFilters,
  emptyTripsFilters,
  hasAnyFilterKey,
  parseTripsFiltersFromStorage,
  serializeIdList,
  serializeTripsFilters,
  withStoredTripsFilterLabels,
} from 'src/app/filters';

@Component({
  selector: 'app-trip-modal',
  templateUrl: './trip-modal.component.html',
  styleUrl: './trip-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TripModalComponent implements OnInit, OnChanges, OnDestroy {
  @Input() trips = initialTripsState;
  readonly tripsWithoutMarker = input<Record<number, boolean>>({});
  readonly positionsDelays = input<Record<number, number>>({});
  @Input() showTripDetails: boolean;
  @Input() selectedTrip: ITrip;
  @Input() tripDetails: ITripDetail;
  @Input() searching: boolean = false;
  @Input() viewMode = TripsViewMode.TRIPS;
  @Output() viewModeChange = new EventEmitter<TripsViewMode>();
  @Output() getTripEventEmitter = new EventEmitter<number>();
  @Output() refreshTripsEventEmitter = new EventEmitter<ITripsRefreshEvent>();
  @Output() clearMap = new EventEmitter();
  @Output() showChangeDriverEventEmitter = new EventEmitter();
  @Output() showChangeVehicleEventEmitter = new EventEmitter();
  @Output() showEditCommentEventEmitter = new EventEmitter();
  @Output() showViewBookingsEventEmitter = new EventEmitter();
  @Output() showViewPickUpsEventEmitter = new EventEmitter();
  @Output() showViewDropOffsEventEmitter = new EventEmitter();
  @Output() showViewChangeOversEventEmitter = new EventEmitter();
  @Output() showAddPaxEventEmitter = new EventEmitter();
  @Output() showSetReadyEventEmitter = new EventEmitter();
  @Output() showSetClosedEventEmitter = new EventEmitter();
  @Output() showDeleteTripEventEmitter = new EventEmitter();
  @Output() showDrivenRouteEventEmitter = new EventEmitter();
  @Output() clearIntervalsEventEmitter = new EventEmitter();
  @Output() newTicketEventEmitter = new EventEmitter<ITrip>();

  public images = images;
  public language: string = 'es';
  public expeditionStatus = ExpeditionStatus;
  public clientsSelected: IBase[] = [];
  public operatorsSelected: IBase[] = [];
  public servicesSelected: IBase[] = [];
  public routesSelected: IBase[] = [];
  public townsSelected: IBase[] = [];
  public driversSelected: IBase[] = [];
  public vehiclesSelected: IBase[] = [];
  public typesSelected: ExpeditionType[] = [];
  public serviceTypesSelected: ServiceType[] = [];
  public statusSelected: ExpeditionStatus[] = [];
  public availabilityIdSelected: number | null = null;
  public itineraryIdSelected: number | null = null;
  public fromSelected?: Date;
  public toSelected?: Date;
  public searchValue: string;

  public bookings: IBooking[];
  public vehicles: IVehicle[];
  public drivers: IUserDriver[];

  public showDrivenRoute = false;
  public expandedDrivenRoute = false;
  public expanded = true;

  private languageSubscription: Subscription;
  private queryParamsSub?: Subscription;

  public TooltipPosition = TooltipPosition;

  public interval: any;

  public readonly serviceOption = serviceOption;
  public readonly routeOption = routeOption;
  public readonly driverOption = driverOption;
  public readonly vehicleOption = vehicleOption;
  public serviceLoader: FilterOptionLoader;
  public routeLoader: FilterOptionLoader;
  public driverLoader: FilterOptionLoader;
  public vehicleLoader: FilterOptionLoader;
  public readonly serviceByIdLoader: FilterOptionByIdLoader;
  public readonly routeByIdLoader: FilterOptionByIdLoader;
  public readonly driverByIdLoader: FilterOptionByIdLoader;
  public readonly vehicleByIdLoader: FilterOptionByIdLoader;

  constructor(
    public literalService: LiteralService,
    public utilsService: UtilsService,
    public languageService: LanguageService,
    public tripsService: TripsService,
    public usersService: UsersService,
    public storageService: StorageService,
    public activatedRoute: ActivatedRoute,
    public router: Router,
    private serviceInfoService: ServiceInfoService,
    private routeService: RouteService,
    private vehiclesService: VehiclesService,
  ) {
    const clientIds = () => this.clientsSelected.map((client) => client.id!);
    this.serviceLoader = serviceLoader(this.serviceInfoService, () => this.townsSelected as ITown[]);
    this.routeLoader = routeLoader(this.routeService, () => this.servicesSelected.map((service) => service.id!));
    this.driverLoader = driverLoader(this.usersService, clientIds);
    this.vehicleLoader = vehicleLoader(this.vehiclesService, clientIds);
    this.serviceByIdLoader = serviceByIdLoader(this.serviceInfoService);
    this.routeByIdLoader = routeByIdLoader(this.routeService);
    this.driverByIdLoader = driverByIdLoader(this.usersService);
    this.vehicleByIdLoader = vehicleByIdLoader(this.vehiclesService);
  }

  get isPositions(): boolean {
    return this.viewMode === TripsViewMode.POSITIONS;
  }

  get collapsed(): boolean {
    return this.isPositions && !this.expanded;
  }

  get basePath(): string {
    return APP.TRIPS_BASE_PATHS[this.viewMode];
  }

  get pageSize(): number {
    return this.isPositions ? APP.POSITIONS_PAGE_SIZE : APP.TRIPS_PAGE_SIZE;
  }

  get strokeBg(): any {
    return {
      'background-color': `${this.selectedTrip.service && this.selectedTrip.service.color || Colors.TRANSPARENT}`
    };
  }

  get greenBg(): any {
    return {
      'background-color': '#0dedab',
    };
  }

  get purpleBg(): any {
    return {
      'background-color': '#8136db',
    };
  }

  get greyBg(): any {
    return {
      'background-color': '#848ea8',
    };
  }

  async ngOnInit() {
    this.languageSubscription = this.languageService.language$.subscribe(
      (language: string) => {
        this.language = language;
      },
    );

    // URL drives filters/page/search; subscribe so back/forward reactively applies.
    this.queryParamsSub = this.activatedRoute.queryParamMap
      .pipe(
        switchMap((params) =>
          from(this.storageService.getTripsFilters()).pipe(
            map((storedFilters) => ({ params, stored: parseTripsFiltersFromStorage(storedFilters) })),
          ),
        ),
      )
      .subscribe(({ params, stored }) => this.applyQueryParamsToState(params, stored));

    this.interval = setInterval(() => {
      if (!this.isPositions) this.getTrips();
    }, 60000);
  }

  private applyQueryParamsToState(params: ParamMap, stored: ITripsFilters | null): void {
    const filters: ITripsFilters = hasAnyFilterKey(params, TRIP_FILTER_KEYS)
      ? withStoredTripsFilterLabels(deserializeTripsFilters(params), stored)
      : emptyTripsFilters();
    this.clientsSelected = filters.selectedClients;
    this.operatorsSelected = filters.selectedOperators;
    this.townsSelected = filters.selectedTowns;
    this.servicesSelected = filters.selectedServices;
    this.routesSelected = filters.selectedRoutes;
    this.driversSelected = filters.selectedDrivers;
    this.vehiclesSelected = filters.selectedVehicles;
    this.typesSelected = filters.selectedTypes;
    this.serviceTypesSelected = filters.selectedServiceTypes;
    this.statusSelected = filters.selectedStatus;
    this.availabilityIdSelected = filters.selectedAvailabilityId;
    this.itineraryIdSelected = filters.selectedItineraryId;
    this.fromSelected = filters.from;
    this.toSelected = filters.to;

    const pageParam = params.get('page');
    if (pageParam) {
      const page = Number(pageParam);
      if (!isNaN(page) && page > 0) this.trips.pagination.actualPage = page;
    } else {
      this.trips.pagination.actualPage = 1;
    }

    this.searchValue = params.get('search') ?? '';
  }

  async getTrips() {
    this.trips.pagination.numberOfElements = this.pageSize;
    this.refreshTripsEventEmitter.emit({
      snapshot: this.activatedRoute.snapshot,
      clientsSelected: this.clientsSelected,
      operatorsSelected: this.operatorsSelected,
      servicesSelected: this.servicesSelected,
      routesSelected: this.routesSelected,
      driversSelected: this.driversSelected,
      vehiclesSelected: this.vehiclesSelected,
      typesSelected: this.typesSelected,
      serviceTypesSelected: this.serviceTypesSelected,
      statusSelected: this.statusSelected,
      availabilityIdSelected: this.availabilityIdSelected,
      itineraryIdSelected: this.itineraryIdSelected,
      searchValue: this.searchValue,
      from: this.fromSelected,
      to: this.toSelected
    });
  }

  changePage(event: { isNext: boolean; specificPage?: number }) {
    const current = this.trips.pagination.actualPage;
    const newPage =
      event.specificPage ?? (event.isNext ? current + 1 : current - 1);
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { page: newPage },
      queryParamsHandling: 'merge',
    });
  }

  getTripDetails(event: any) {
    if (this.showTripDetails) {
      this.goBack();
      this.router.navigate([this.basePath], {
        queryParams: this.activatedRoute.snapshot.queryParams,
      });
    } else {
      this.getTripEventEmitter.emit(event);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['viewMode'] && this.isPositions && this.showDrivenRoute) this.hideDrivenRoute();
  }

  private hideDrivenRoute() {
    this.showDrivenRoute = false;
    this.expandedDrivenRoute = false;
    this.showDrivenRouteEventEmitter.emit(this.showDrivenRoute);
  }

  goBack() {
    this.hideDrivenRoute();
    this.clearMap.emit();
  }

  public filter(event: ITripsFilters) {
    const currentFilters = this.currentFilters();
    const selectedClients = event?.selectedClients ?? [];
    const clientsChanged = serializeIdList(selectedClients) !== serializeIdList(currentFilters.selectedClients);
    const filters: ITripsFilters = {
      selectedClients,
      selectedOperators: event?.selectedOperators ?? [],
      selectedTowns: event?.selectedTowns ?? [],
      selectedServices: event?.selectedServices ?? [],
      selectedRoutes: event?.selectedRoutes ?? [],
      selectedDrivers: clientsChanged ? [] : currentFilters.selectedDrivers,
      selectedVehicles: clientsChanged ? [] : currentFilters.selectedVehicles,
      selectedTypes: event?.selectedTypes ?? [],
      selectedServiceTypes: event?.selectedServiceTypes ?? [],
      selectedStatus: event?.selectedStatus ?? [],
      from: event?.from,
      to: event?.to,
      selectedAvailabilityId: currentFilters.selectedAvailabilityId,
      selectedItineraryId: currentFilters.selectedItineraryId,
    };
    this.applyFilters(filters);
  }

  onServicesFilter(services: IBase[]) {
    const currentFilters = this.currentFilters();
    const servicesChanged = serializeIdList(services) !== serializeIdList(currentFilters.selectedServices);
    this.applyFilters({
      ...currentFilters,
      selectedServices: services,
      selectedRoutes: servicesChanged ? [] : currentFilters.selectedRoutes,
    });
  }

  onColumnFilter(changes: Partial<ITripsFilters>) {
    this.applyFilters({ ...this.currentFilters(), ...changes });
  }

  private currentFilters(): ITripsFilters {
    return {
      ...deserializeTripsFilters(this.activatedRoute.snapshot.queryParamMap),
      selectedServices: this.servicesSelected,
      selectedRoutes: this.routesSelected,
      selectedDrivers: this.driversSelected,
      selectedVehicles: this.vehiclesSelected,
    };
  }

  private applyFilters(filters: ITripsFilters) {
    this.storageService.setTripsFilters(filters);
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { ...serializeTripsFilters(filters), page: null },
      queryParamsHandling: 'merge',
    });
  }

  search(event: any) {
    const newSearch = event || null;
    const currentSearch =
      this.activatedRoute.snapshot.queryParamMap.get('search') || null;
    if (newSearch === currentSearch) {
      this.trips.isFetchingTrips = true;
      this.trips.value = [];
      this.getTrips();
      return;
    }
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { search: event || null, page: null },
      queryParamsHandling: 'merge',
    });
  }

  shouldHaveStarted(dateTime?: Date): boolean {
    return dateTime ? new Date(dateTime) < new Date() : false;
  }

  getStopColor(index: number) {
    if (this.tripDetails.stops[index].realTime) return this.greyBg;
    if (index === 0) return this.purpleBg;
    if (index === this.tripDetails.stops.length - 1) return this.greenBg;
    return this.strokeBg;
  }

  getLineColor(index: number) {
    if (this.tripDetails.stops.length === index + 1) return '';
    return this.tripDetails.stops[index + 1].realTime
      ? this.greyBg
      : this.strokeBg;
  }

  getStopTime(stop: ITripStop, isDeparture: boolean): IStopTimeDisplay {
    const estimated =
      (isDeparture && stop.estimatedDepartureTime) || stop.estimatedTime;
    const theoreticalDescription = this.literalService.get(
      `trips.tooltips.theoreticalTime.${isDeparture ? 'departure' : 'arrival'}`,
    );
    const theoreticalRow = `${estimated} - ${theoreticalDescription}`;
    const estimatedDisplay: IStopTimeDisplay = {
      value: estimated,
      styleClass: 'font-semibold',
      tooltip: this.utilsService.capitalize(theoreticalDescription),
    };
    if (
      this.selectedTrip.status !== ExpeditionStatus.ONGOING &&
      this.selectedTrip.status !== ExpeditionStatus.CLOSED
    ) {
      return estimatedDisplay;
    }
    if (stop.skipped) {
      const value = stop.realTime || stop.estimatedTime;
      return {
        value,
        styleClass: 'font-semibold line-through',
        tooltip: `<s>${value}</s><label> - ${this.literalService.get('trips.tooltips.omitedStops', true)}</label>\n${theoreticalRow}`,
      };
    }
    const real = isDeparture ? stop.realDepartureTime : stop.realTime;
    if (real) {
      return {
        value: real,
        styleClass: 'font-bold',
        tooltip: `<b>${real}</b><label> - ${this.literalService.get(`trips.tooltips.realTime.${isDeparture ? 'departure' : 'arrival'}`)}</label>\n${theoreticalRow}`,
      };
    }
    const foreseen = this.getForeseenTime(stop, isDeparture);
    if (foreseen) {
      return {
        value: foreseen,
        styleClass: 'italic',
        tooltip: `<i>${foreseen}</i><label> - ${this.literalService.get(`trips.tooltips.foreseenTime.${isDeparture ? 'departure' : 'arrival'}`)}</label\n${theoreticalRow}`,
      };
    }
    return estimatedDisplay;
  }

  private getForeseenTime(
    stop: ITripStop,
    isDeparture: boolean,
  ): string | undefined {
    if (!stop.foreseenTime || !isDeparture) return stop.foreseenTime;
    if (!stop.estimatedDepartureTime) return stop.foreseenTime;
    const delay =
      this.toMinutes(stop.foreseenTime) - this.toMinutes(stop.estimatedTime);
    return this.addMinutes(stop.estimatedDepartureTime, delay);
  }

  private toMinutes(time: string): number {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  }

  private addMinutes(time: string, minutes: number): string {
    const total = (this.toMinutes(time) + minutes + 1440) % 1440;
    return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
  }

  onShowDrivenRouteChange(event: any) {
    this.showDrivenRoute = event.target.checked;
    this.showDrivenRouteEventEmitter.emit(this.showDrivenRoute);
  }

  ngOnDestroy(): void {
    this.languageSubscription.unsubscribe();
    this.queryParamsSub?.unsubscribe();
    clearInterval(this.interval);
  }
}
